import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { ClubKiezerService, StudioInstaller } from '../../services/club-kiezer.service';
import { ClubKeuze, eersteKeuze, groepeerKeuzes } from '../../helpers/club-kiezer';
import { telefoon } from '../../helpers/telefoon';

export interface ClubKiezerData {
  /** In een venster (wisselen vanuit een club): met sluitknop. */
  sluitbaar?: boolean;
}

interface Groep {
  sleutel: string;
  titel: string;
  items: ClubKeuze[];
}

/**
 * Clubkiezer voor een super-admin (eigenaar 24-10-2026, variant D): na het
 * inloggen met zijn eigen account (pagina /auth/kies-club) en vanuit het
 * accountmenu ("Switch club"). Zoeken, recent geopende clubs, en daarna
 * clubs, leagues en taggingteams op alfabet. Kiezen opent de club met de
 * rechten van een clubbeheerder, op naam van de super-admin; daarna laadt de
 * app opnieuw, zodat rechten, ploegen, socket en opslagbewijs bij de nieuwe
 * club horen (zelfde reden als AuthService.wisselOmgeving).
 */
@Component({
  selector: 'app-club-kiezer',
  standalone: true,
  imports: [TranslateModule, SvgIconComponent],
  templateUrl: './club-kiezer.component.html',
  styleUrls: ['./club-kiezer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.telefoon]': 'opTelefoon()',
    '[class.blad]': 'sluitbaar && opTelefoon()',
  },
})
export class ClubKiezerComponent implements OnInit, AfterViewInit {
  private readonly kiezer = inject(ClubKiezerService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(DialogRef, { optional: true });
  private readonly data = inject<ClubKiezerData | null>(DIALOG_DATA, { optional: true });

  @ViewChild('zoekveld') private zoekveld?: ElementRef<HTMLInputElement>;

  public readonly sluitbaar = !!this.data?.sluitbaar;
  /** Telefoon, staand of liggend: in een venster dan een blad van onderen. */
  public readonly opTelefoon = telefoon();
  /** De club die nu open is; leeg direct na het inloggen. */
  public readonly huidig = this.authService.user?.impersonated_by ? this.authService.user.club_id : '';

  public readonly keuzes = signal<ClubKeuze[] | null>(null);
  public readonly fout = signal(false);
  public readonly zoek = signal('');
  /** De club die nu geopend wordt. */
  public readonly bezig = signal<string | null>(null);
  /** Installer van de studio-app, voor een nieuwe studio-pc (28-10-2026, variant A). */
  public readonly installer = signal<StudioInstaller | null>(null);

  private readonly recent = this.kiezer.recent;
  private readonly groepen = computed(() => groepeerKeuzes(this.keuzes() ?? [], this.zoek(), this.recent));

  public readonly lijst = computed<Groep[]>(() => {
    const g = this.groepen();
    return [
      { sleutel: 'recent', titel: 'CLUB_KIEZER.recent', items: g.recent },
      { sleutel: 'clubs', titel: 'CLUB_KIEZER.clubs', items: g.clubs },
      { sleutel: 'leagues', titel: 'CLUB_KIEZER.leagues', items: g.leagues },
      { sleutel: 'tagging', titel: 'CLUB_KIEZER.tagging', items: g.tagging },
    ].filter((groep) => groep.items.length > 0);
  });

  public readonly nietsGevonden = computed(() => this.keuzes() !== null && this.lijst().length === 0);

  ngOnInit(): void {
    this.kiezer.lijst().subscribe({
      next: (keuzes) => this.keuzes.set(keuzes ?? []),
      error: () => this.fout.set(true),
    });
    // Niet op de telefoon: daar installeer je geen Windows-app. Lukt het niet
    // (geen recht, geen link), dan staat er gewoon geen link.
    if (!this.opTelefoon()) {
      this.kiezer.studioInstaller().subscribe({
        next: (installer) => this.installer.set(installer),
        error: () => this.installer.set(null),
      });
    }
  }

  ngAfterViewInit(): void {
    // Meteen typen. Niet op een aanraakscherm: daar schuift het toetsenbord
    // anders over de lijst voordat je iets gezien hebt.
    if (typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches) {
      this.zoekveld?.nativeElement.focus();
    }
  }

  public icoon(keuze: ClubKeuze): string {
    if (keuze.kind === 'league') {
      return 'assets/icons/league.svg';
    }
    return keuze.kind === 'tagging' ? 'assets/icons/tag.svg' : 'assets/icons/club_album.svg';
  }

  public onZoek(waarde: string): void {
    this.zoek.set(waarde);
  }

  public kiesEerste(): void {
    const eerste = eersteKeuze(this.groepen());
    if (eerste) {
      this.kies(eerste);
    }
  }

  public kies(keuze: ClubKeuze): void {
    if (this.bezig()) {
      return;
    }
    if (keuze.club === this.huidig) {
      this.sluit();
      return;
    }
    this.bezig.set(keuze.club);
    this.kiezer.open(keuze.club).subscribe({
      next: (token) => {
        this.kiezer.onthoud(keuze.club);
        this.authService.loginMetToken(token);
        window.location.assign('/');
      },
      error: () => {
        this.bezig.set(null);
        this.toastr.error(this.translate.instant('CLUB_KIEZER.error_open', { naam: keuze.name }));
      },
    });
  }

  public sluit(): void {
    this.dialogRef?.close();
  }

  /** Terug naar het inlogscherm; de beheerderssessie is dan weg. */
  public stop(): void {
    this.dialogRef?.close();
    this.authService.logout();
    void this.router.navigateByUrl('/auth/login').then(() => window.location.reload());
  }
}
